import { useCallback, useEffect, useRef, useState } from 'react';
import { HFBox, HFButton, HFCard, HFCheckbox, HFModal, HFSelect, HFSkeleton, HFTextField, HFTooltip, type HFSelectProps } from '@hf/designSystem/v1';
import {
  OMS_COLUMN_LAYOUT_NAME,
  OMS_PANEL_IDS,
  omsLayoutListSchema,
  omsLayoutSchema,
  type OmsCustomLayout,
  type OmsCustomLayoutPanel,
  type OmsLayout,
  type OmsLayoutColumn,
  type OmsLayoutList,
  type OmsLayoutSort,
  type OmsPanelId,
  type OmsSharedView,
} from '@hf/sharedContract';
import { useHttp } from '@core/utils/http';
import { useSharedViews } from '../hooks/useSharedViews';
import type { MonitorOrders } from '../hooks/useMonitorOrders';
import { OrderBlotter } from './OrderBlotter';
import { PositionsGrid } from './PositionsGrid';
import { TableSkeleton } from './TableSkeleton';
import { TradeBlotter } from './TradeBlotter';

const PANEL_ORDER: OmsPanelId[] = [...OMS_PANEL_IDS];
const PANELS: Record<OmsPanelId, { label: string; description: string }> = {
  orders: { label: 'Order Blotter', description: 'Parent orders, allocations and fills' },
  fills: { label: 'Trade Blotter (fills)', description: 'One row per execution' },
  positions: { label: 'Positions', description: 'Opening, Filled, Working, Closing, C+O' },
};
const EMPTY: OmsCustomLayout = { arrangement: 'stacked', panels: [], filters: {} };
const HELP =
  'Each panel keeps its own filters (book, exchange, product) and columns, apart from your home tabs. Layouts are shared with everyone in your company, who can open, update or delete them.';
const SECTION_LABEL_SX = { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary', mb: 1 } as const;
const SECTION_SX = { display: 'flex', flexDirection: 'column' } as const;
const PANEL_OPTION_SX = {
  px: 1.5,
  py: 1,
  '& .MuiFormControlLabel-root': { ml: -0.5, mr: 0 },
  '& .MuiCheckbox-root': { p: 0.5 },
  '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 600 },
} as const;
const PREVIEW_BLOCK_SX = { bgcolor: 'currentColor', opacity: 0.3, borderRadius: 0.5 } as const;
const ARRANGEMENTS = [
  { id: 'stacked', label: 'Stacked' },
  { id: 'sideBySide', label: 'Side by side' },
] as const;
const PANEL_MIN_HEIGHT = 360;
const NAME_MAX_LENGTH = 64;
const LAST_LAYOUT_PATH = `/oms/layouts/custom/${OMS_COLUMN_LAYOUT_NAME}`;

/** Picks one of the company's saved layouts. The first option, `emptyLabel`, means none is open. */
function SavedViewSelect({
  'aria-label': ariaLabel,
  emptyLabel,
  views,
  value,
  onChange,
  formControlSx,
}: {
  'aria-label': string;
  emptyLabel: string;
  views: OmsSharedView[];
  value: string;
  onChange: (id: string) => void;
  formControlSx?: HFSelectProps['formControlSx'];
}) {
  const selected = views?.find((view) => view.id === value);
  return (
    <HFSelect
      aria-label={ariaLabel}
      value={value}
      displayEmpty
      options={[
        { value: '', label: emptyLabel },
        ...views.map((view) => ({
          value: view.id,
          label: (
            <HFBox component="span" sx={{ display: 'flex', gap: 1, minWidth: 0, width: '100%' }}>
              <HFBox component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {view.name}
              </HFBox>
              <HFBox component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{`(${view.createdBy})`}</HFBox>
            </HFBox>
          ),
        })),
      ]}
      onChange={onChange}
      renderValue={() => selected?.name ?? emptyLabel}
      MenuProps={{ slotProps: { paper: { sx: { minWidth: 280 } } } }}
      formControlSx={formControlSx}
      sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
    />
  );
}

/** Asks for the name of a new saved layout. */
function SaveViewModal({
  open,
  title,
  label,
  saving,
  onSave,
  onClose,
}: {
  open: boolean;
  title: string;
  label: string;
  saving: boolean;
  onSave: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  useEffect(() => {
    if (open) setName('');
  }, [open]);
  const trimmed = name.trim();

  return (
    <HFModal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="xs"
      fullWidth
      actions={
        <>
          <HFButton variant="outlined" onClick={onClose}>
            Cancel
          </HFButton>
          <HFButton loading={saving} disabled={!trimmed} onClick={() => onSave(trimmed)}>
            Save
          </HFButton>
        </>
      }
    >
      <HFTextField
        label={label}
        size="small"
        value={name}
        onChange={(event) => setName(event.target.value)}
        slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
      />
    </HFModal>
  );
}

/** Confirms a delete. The delete applies to the whole company. */
function DeleteViewModal({
  open,
  title,
  name,
  deleting,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  name: string;
  deleting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <HFModal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="xs"
      fullWidth
      actions={
        <>
          <HFButton variant="outlined" onClick={onClose}>
            Cancel
          </HFButton>
          <HFButton color="error" loading={deleting} onClick={onConfirm}>
            Delete
          </HFButton>
        </>
      }
    >
      {`"${name}" is removed for everyone in your company.`}
    </HFModal>
  );
}

/** Stacked panels take the full width. Side by side, two share the first row and a third takes a row of its own. */
function withWidths(ids: OmsPanelId[], arrangement: OmsCustomLayout['arrangement']): OmsCustomLayoutPanel[] {
  return ids.map((id, index) => ({ id, width: arrangement === 'stacked' || ids.length === 1 || index === 2 ? 100 : 50 }));
}

/** A thumbnail of two panels, on top of each other or next to each other. */
function ArrangementPreview({ arrangement }: { arrangement: OmsCustomLayout['arrangement'] }) {
  const stacked = arrangement === 'stacked';
  return (
    <HFBox
      aria-hidden
      sx={{
        width: '100%',
        height: 36,
        display: 'grid',
        gap: 0.5,
        gridTemplateColumns: stacked ? '1fr' : '1fr 1fr',
        gridTemplateRows: stacked ? '1fr 1fr' : '1fr',
      }}
    >
      <HFBox sx={PREVIEW_BLOCK_SX} />
      <HFBox sx={PREVIEW_BLOCK_SX} />
    </HFBox>
  );
}

/** The settings card's shape while the saved layouts load. */
function SettingsSkeleton() {
  return (
    <HFBox data-testid="custom-layout-settings-skeleton" aria-hidden sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {[32, 132, 52].map((height, index) => (
        <HFBox key={index} sx={SECTION_SX}>
          <HFSkeleton variant="text" width={88} height={14} sx={{ mb: 1 }} />
          <HFSkeleton variant="rounded" height={height} />
        </HFBox>
      ))}
      <HFBox sx={{ ...SECTION_SX, gap: 1 }}>
        <HFSkeleton variant="rounded" height={32} />
        <HFSkeleton variant="rounded" height={32} />
      </HFBox>
      <HFSkeleton variant="rounded" height={48} />
    </HFBox>
  );
}

/** Picks which grids show and how they sit. A layout is shared with the company, and keeps each shown grid's filters and columns. */
export function CustomLayout({ date, monitor }: { date?: string; monitor: MonitorOrders }) {
  const { request } = useHttp();
  const { views, saving, save, remove } = useSharedViews();
  const [selectedId, setSelectedId] = useState('');
  /** The layout this user last opened or saved: undefined while their custom row loads, null when it names none. */
  const [lastId, setLastId] = useState<string | null | undefined>(undefined);
  const [restored, setRestored] = useState(false);
  const requestRef = useRef(request);
  requestRef.current = request;
  const lastVersion = useRef<number | undefined>(undefined);
  const lastWrites = useRef<Promise<void>>(Promise.resolve());
  const [draft, setDraft] = useState<OmsCustomLayout>(EMPTY);
  const [opened, setOpened] = useState(0);
  const [naming, setNaming] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const filtersRef = useRef<OmsCustomLayout['filters']>({});
  const columnsRef = useRef<Partial<Record<OmsPanelId, { columns: OmsLayoutColumn[]; sort?: OmsLayoutSort }>>>({});
  const selected = views?.find((view) => view.id === selectedId);
  const ids = draft.panels.map((panel) => panel.id);
  const sideBySide = draft.arrangement === 'sideBySide' && draft.panels.length > 1;

  useEffect(() => {
    let cancelled = false;
    void request<OmsLayoutList>('/oms/layouts?tab=custom', { responseSchema: omsLayoutListSchema, toastError: false }).then((result) => {
      if (cancelled) return;
      const stored = result.status === 200 ? result.data?.layouts.find((item) => item.layoutName === OMS_COLUMN_LAYOUT_NAME) : undefined;
      lastVersion.current = stored?.version;
      setLastId(stored?.customLayoutId ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [request]);

  /** Opens a saved layout, or clears the panels for `''`. The grids remount, so their saved filters apply. */
  const open = useCallback(
    (id: string) => {
      const layout = views?.find((view) => view.id === id)?.custom ?? EMPTY;
      setSelectedId(id);
      setDraft(layout);
      filtersRef.current = layout.filters;
      columnsRef.current = Object.fromEntries(
        layout.panels.flatMap(({ id: panelId, columns, sort }) => (columns ? [[panelId, { columns, sort }]] : [])),
      );
      setOpened((count) => count + 1);
    },
    [views],
  );
  useEffect(() => {
    if (restored || views === null || lastId === undefined) return;
    if (lastId && views.some((view) => view.id === lastId)) open(lastId);
    setRestored(true);
  }, [restored, views, lastId, open]);

  /** Keeps `id` (or none) as the layout this user returns to. Background state: one write at a time, no toast. */
  const rememberLayout = (id?: string) => {
    lastWrites.current = lastWrites.current.then(async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const layout: OmsLayout = {
          tab: 'custom',
          layoutName: OMS_COLUMN_LAYOUT_NAME,
          columns: [],
          ...(id ? { customLayoutId: id } : {}),
          ...(lastVersion.current != null ? { version: lastVersion.current } : {}),
        };
        const result = await requestRef.current<OmsLayout>(LAST_LAYOUT_PATH, {
          method: 'PUT',
          json: layout,
          requestSchema: omsLayoutSchema,
          responseSchema: omsLayoutSchema,
          toastError: false,
        });
        if (result.status === 200 && result.data) {
          lastVersion.current = result.data.version;
          return;
        }
        if (result.status !== 409) return;
        const listed = await requestRef.current<OmsLayoutList>('/oms/layouts?tab=custom', {
          responseSchema: omsLayoutListSchema,
          toastError: false,
          coalesce: 'url',
        });
        lastVersion.current = listed.data?.layouts.find((item) => item.layoutName === OMS_COLUMN_LAYOUT_NAME)?.version;
      }
    });
  };
  const choose = (id: string) => {
    open(id);
    rememberLayout(id || undefined);
  };
  const toggle = (id: OmsPanelId, on: boolean) => {
    setDraft({
      ...draft,
      panels: withWidths(
        PANEL_ORDER.filter((item) => (item === id ? on : ids.includes(item))),
        draft.arrangement,
      ),
    });
  };
  const arrange = (arrangement: OmsCustomLayout['arrangement']) => {
    setDraft({ ...draft, arrangement, panels: withWidths(ids, arrangement) });
  };
  const payload = (): OmsCustomLayout => ({
    arrangement: draft.arrangement,
    panels: draft.panels.map(({ id: panelId, width }) => ({ id: panelId, width, ...columnsRef.current[panelId] })),
    filters: Object.fromEntries(ids.flatMap((id) => (filtersRef.current[id] ? [[id, filtersRef.current[id]]] : []))),
  });
  const saveLayout = () => {
    if (selected) void save(selected.id, selected.name, payload());
    else setNaming(true);
  };
  const create = async (name: string) => {
    const view = await save(null, name, payload());
    if (!view) return;
    setSelectedId(view.id);
    setNaming(false);
    rememberLayout(view.id);
  };
  const confirmDelete = async () => {
    if (!selected || !(await remove(selected.id))) return;
    setConfirming(false);
    choose('');
  };

  const remember = (id: OmsPanelId) => (filters: OmsCustomLayout['filters'][OmsPanelId]) => {
    filtersRef.current = { ...filtersRef.current, [id]: filters };
  };
  const panel = (id: OmsPanelId) => {
    const saved = columnsRef.current[id];
    const shared = {
      compact: true,
      date,
      onFiltersChange: remember(id),
      initialColumns: saved?.columns,
      onColumnsChange: (columns: OmsLayoutColumn[], sort?: OmsLayoutSort) => {
        columnsRef.current = { ...columnsRef.current, [id]: { columns, sort } };
      },
    };
    const key = `${id}-${opened}`;
    if (id === 'orders') {
      return (
        <OrderBlotter
          {...shared}
          key={key}
          initialFilters={draft.filters.orders}
          initialSort={saved?.sort}
          orders={monitor.orders}
          ordersFailed={monitor.failed}
          positions={monitor.positions}
        />
      );
    }
    if (id === 'positions') {
      return (
        <PositionsGrid
          {...shared}
          key={key}
          initialFilters={draft.filters.positions}
          positions={monitor.positionList}
          failed={monitor.positionsFailed}
        />
      );
    }
    return <TradeBlotter {...shared} key={key} initialFilters={draft.filters.fills} initialSort={saved?.sort} />;
  };

  return (
    <HFBox
      component="section"
      aria-label="Custom layout"
      aria-busy={!restored}
      sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0, alignItems: 'flex-start' }}
    >
      <HFCard component="aside" aria-label="Layout settings" sx={{ width: 240, flexShrink: 0, height: 'auto', maxHeight: '100%', overflowY: 'auto' }}>
        {!restored ? (
          <SettingsSkeleton />
        ) : (
          <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <HFBox sx={SECTION_SX}>
              <HFBox sx={SECTION_LABEL_SX}>SAVED LAYOUTS</HFBox>
              <SavedViewSelect
                aria-label="Saved layouts"
                emptyLabel="New layout"
                views={views ?? []}
                value={selectedId}
                onChange={choose}
                formControlSx={{ width: '100%', '& .MuiInputBase-root': { height: 32, fontSize: 13 } }}
              />
            </HFBox>
            <HFBox sx={SECTION_SX}>
              <HFBox sx={SECTION_LABEL_SX}>PANELS</HFBox>
              <HFBox role="group" aria-label="Panels" sx={{ ...SECTION_SX, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {PANEL_ORDER.map((id, index) => (
                  <HFBox key={id} sx={{ ...PANEL_OPTION_SX, ...(index > 0 ? { borderTop: 1, borderColor: 'divider' } : {}) }}>
                    <HFCheckbox size="small" label={PANELS[id].label} checked={ids.includes(id)} onChange={(on) => toggle(id, on)} />
                    <HFBox sx={{ pl: 3, fontSize: 12, lineHeight: 1.4, color: 'text.secondary' }}>{PANELS[id].description}</HFBox>
                  </HFBox>
                ))}
              </HFBox>
            </HFBox>
            <HFBox sx={SECTION_SX}>
              <HFBox sx={SECTION_LABEL_SX}>ARRANGEMENT</HFBox>
              <HFBox role="group" aria-label="Arrangement" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {ARRANGEMENTS.map(({ id, label }) => {
                  const active = draft.arrangement === id;
                  return (
                    <HFTooltip key={id} title={label}>
                      <HFButton
                        variant="outlined"
                        aria-label={label}
                        aria-pressed={active}
                        onClick={() => arrange(id)}
                        sx={{
                          minWidth: 0,
                          p: 1,
                          color: active ? 'primary.main' : 'text.secondary',
                          borderColor: active ? 'primary.main' : 'divider',
                          bgcolor: active ? 'action.selected' : 'transparent',
                        }}
                      >
                        <ArrangementPreview arrangement={id} />
                      </HFButton>
                    </HFTooltip>
                  );
                })}
              </HFBox>
            </HFBox>
            <HFBox sx={{ ...SECTION_SX, gap: 1 }}>
              <HFButton
                fullWidth
                size="small"
                loading={saving}
                disabled={draft.panels.length === 0}
                onClick={saveLayout}
                sx={{ minHeight: 32, fontSize: 13 }}
              >
                Save layout
              </HFButton>
              <HFButton
                fullWidth
                size="small"
                variant="outlined"
                color="error"
                disabled={!selected}
                onClick={() => setConfirming(true)}
                sx={{ minHeight: 32, fontSize: 13 }}
              >
                Delete layout
              </HFButton>
            </HFBox>
            <HFBox sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.5 }}>{HELP}</HFBox>
          </HFBox>
        )}
      </HFCard>
      <HFBox sx={{ flex: 1, minWidth: 0, minHeight: 0, alignSelf: 'stretch', display: 'flex', flexDirection: 'column' }}>
        {!restored ? (
          <TableSkeleton testId="custom-layout-skeleton" />
        ) : draft.panels.length === 0 ? (
          <HFBox sx={{ px: 2, py: 3, color: 'text.secondary', fontSize: 14 }}>Pick at least one panel.</HFBox>
        ) : (
          <HFBox
            sx={
              sideBySide
                ? {
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: `${draft.panels[0]!.width}fr ${draft.panels[1]!.width}fr`,
                    gridAutoRows: `minmax(${PANEL_MIN_HEIGHT}px, 1fr)`,
                  }
                : { flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }
            }
          >
            {draft.panels.map((item, index) => (
              <HFBox
                key={item.id}
                data-testid={`panel-${item.id}`}
                sx={{
                  minWidth: 0,
                  minHeight: PANEL_MIN_HEIGHT,
                  display: 'flex',
                  flexDirection: 'column',
                  ...(sideBySide ? (index === 2 ? { gridColumn: '1 / -1' } : {}) : { flex: '1 1 0' }),
                }}
              >
                {panel(item.id)}
              </HFBox>
            ))}
          </HFBox>
        )}
      </HFBox>
      <SaveViewModal
        open={naming}
        title="New layout"
        label="Layout name"
        saving={saving}
        onSave={(name) => void create(name)}
        onClose={() => setNaming(false)}
      />
      <DeleteViewModal
        open={confirming}
        title="Delete layout"
        name={selected?.name ?? ''}
        deleting={saving}
        onConfirm={() => void confirmDelete()}
        onClose={() => setConfirming(false)}
      />
    </HFBox>
  );
}
